import { useIsClient } from '@/hooks/useIsClient';
import carUrl from '@/static/assets/car/driving/car.svg';
import carFsdUrl from '@/static/assets/car/driving/car-fsd.svg';
import cloudUrl from '@/static/assets/car/driving/clouds.png';
import screenUrl from '@/static/assets/car/driving/screen.svg';
import screenFsdUrl from '@/static/assets/car/driving/screen-fsd.svg';
import wheelFsdUrl from '@/static/assets/car/driving/wheel-fsd.svg';
import { Box, Flex, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import dynamic from 'next/dynamic';
import { rgba } from 'polished';
/* eslint-disable jsx-a11y/accessible-emoji */
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import Image from 'next/image';
import React, { useContext, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';
import { InvisiButton } from './Common';
import GameModeTitle from './GameModeTitle';
import SpeedGauge from './SpeedGauge';
import Title from './Title';
import type { CarMode } from '../types';
import type { Environment, RoadGeneration } from './driving';
import { useDriving } from './useDriving';
import { CarPageContext } from '../CarPageContext';

const CarScreen = dynamic(() => import('./CarScreen'));
const TeslaCarScreen = dynamic(() => import('@/components/fsd/TeslaCarScreen'));

export interface ScreenPosition {
  width: string;
  left: string;
  bottom: string;
  track?: {
    top?: string;
    left?: string;
    width?: string;
  };
}

export interface WheelPosition {
  left: string;
  bottom: string;
}

export interface MinimapPosition {
  width?: string;
  height?: string;
  left?: string;
  bottom?: string;
}

export interface CheckImagePosition {
  bottom?: string;
  width?: string;
  height?: string;
}

const DEFAULT_SCREEN_POSITION: ScreenPosition = {
  width: '354px',
  left: '1096px',
  bottom: '95px',
};

const DEFAULT_WHEEL_POSITION: WheelPosition = {
  left: '227px',
  bottom: '-426px',
};

export interface SceneTransformValues {
  translateX?: string;
  translateY?: string;
  scale?: number;
}

export interface SceneTransform {
  mobile?: SceneTransformValues;
  tablet?: SceneTransformValues;
  desktop?: SceneTransformValues;
}

export interface DrivingExperienceProps {
  mode?: CarMode;
  showPlayButton?: boolean;
  fadingMask?: boolean;
  screenWrapperPosition?: ScreenPosition;
  wheelPosition?: WheelPosition;
  syncWheelYPosition?: boolean;
  screenCanvasPosition?: MinimapPosition;
  secondaryScreenView?: Environment['secondaryScreenView'];
  checkPosition?: CheckImagePosition;
  overrideMobileAnimationStop?: boolean;
  sceneTransform?: SceneTransform;
  randomSeed?: number;
  roadGeneration?: RoadGeneration;
  showMinimap?: boolean;
}

export interface DrivingProps extends DrivingExperienceProps {
  title: string;
  mobileTitle?: string;
  subtitle: string;
  belowCtaText?: string;
  customUrl?: string;
  mobileTitlePaddingTop?: string;
  excludedParams?: string[];
}

interface ScreenForegroundProps {
  screenWrapperPosition: ScreenPosition;
}

interface WheelProps {
  wheelPosition: WheelPosition;
}

const Section = styled(Flex).attrs({ as: 'section' })`
  contain: layout paint style;
`;

interface SceneProps {
  $sceneTransform?: SceneTransform;
}

const Scene = styled(Box)<SceneProps>`
  width: 100%;
  transition: opacity 0.8s ease;
  user-select: none;
  contain: layout style;
  will-change: opacity;

  ${({ $sceneTransform }) => {
    const translateX = $sceneTransform?.desktop?.translateX ?? '0px';
    const translateY = $sceneTransform?.desktop?.translateY ?? '0px';
    const scale = $sceneTransform?.desktop?.scale ?? 1;
    return MEDIA_QUERIES.desktop`
        transform: translate3d(${translateX}, ${translateY}, 0) scale(${scale});
    `;
  }}

  ${({ $sceneTransform }) => {
    const translateX = $sceneTransform?.tablet?.translateX ?? '200px';
    const translateY = $sceneTransform?.tablet?.translateY ?? '0px';
    const scale = $sceneTransform?.tablet?.scale ?? 1;
    return MEDIA_QUERIES.tablet`
        transform: translate3d(${translateX}, ${translateY}, 0) scale(${scale});
    `;
  }}

  ${({ $sceneTransform }) => {
    const translateX = $sceneTransform?.mobile?.translateX ?? '170px';
    const translateY = $sceneTransform?.mobile?.translateY ?? '155px';
    const scale = $sceneTransform?.mobile?.scale ?? 0.7;
    return MEDIA_QUERIES.mobile`
        transform: translate3d(${translateX}, ${translateY}, 0) scale(${scale});
    `;
  }}
`;

const Canvas = styled(Box).attrs({
  as: 'canvas',
})`
  position: absolute;
  z-index: 0;
  width: 100%;
  height: auto;
  top: 50%;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translate3d(0, -50%, 0);
  backface-visibility: hidden;
`;

interface ContainerProps {
  $isTeslaMode?: boolean;
}

const Container = styled(Box)<ContainerProps>`
  position: relative;
  transform: translate3d(0, 0, 0);
  z-index: 0;
  min-width: 1920px;
  max-width: 1920px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  contain: layout paint style;

  ${({ $isTeslaMode }) =>
    $isTeslaMode &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 80px;
      background: linear-gradient(to bottom, transparent, #f7f7f7);
      pointer-events: none;
      z-index: 10;
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      height: 80px;
      width: 100%;
      background: #f7f7f7;
      z-index: 0;
    }
  `}

  @media (min-width: 1021px) and (max-width: 1920px) {
    min-width: 1920px;
    max-width: 1920px;
  }

  @media (min-width: 1021px) and (max-width: 1440px) {
    height: 645px;

    ${Scene} {
      transform: translate3d(0, 155px, 0) scale(0.7);
    }

    ${Canvas} {
      margin-top: -60px;
    }
  }

  @media (min-width: 1281px) and (max-width: 1440px) {
    height: 685px;

    ${Canvas} {
      margin-top: -50px;
    }
  }

  @media (min-width: 1921px) {
    min-width: 3840px;
    max-width: 3840px;
  }

  @media (min-width: 1921px) and (max-width: 1992px) {
    ${Scene} {
      transform: translate3d(0, -20px, 0) scale(0.55);
    }
  }

  @media (min-width: 1993px) and (max-width: 2380px) {
    ${Scene} {
      transform: translate3d(0, -20px, 0) scale(0.6);
    }
  }

  @media (min-width: 2381px) and (max-width: 2600px) {
    height: 1000px;

    ${Scene} {
      transform: translate3d(0, -80px, 0) scale(0.65);
    }

    ${Canvas} {
      margin-top: 120px;
    }
  }

  @media (min-width: 2601px) and (max-width: 2688px) {
    ${Scene} {
      transform: translate3d(0, -20px, 0) scale(0.7);
    }
  }

  @media (min-width: 2689px) and (max-height: 1270px) {
    ${Scene} {
      transform: translate3d(0, -250px, 0) scale(0.75);
      height: 1400px;
    }
    height: 1060px;
  }

  @media (min-width: 2880px) {
    height: 1120px;

    ${Scene} {
      transform: translate3d(0, 60px, 0) scale(0.75);
    }
    ${Canvas} {
      margin-top: -110px;
    }
  }
`;

const cloudsAnimationDesktop = keyframes`
    from {
        transform: translate3d(0, 0, 0);
    }
    to {
        transform: translate3d(-2085px, 0, 0);
    }
`;

interface CloudsProps {
  $isPaused?: boolean;
}

const Clouds = styled.div<CloudsProps>`
  position: absolute;
  left: 0;
  pointer-events: none;
  width: 100%;
  overflow-x: hidden;
  height: 255px;
  backface-visibility: hidden;
  contain: strict;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4170px;
    height: 100%;
    background-image: url(${cloudUrl.src});
    background-repeat: repeat-x;
    background-size: auto 100%;
    animation: ${cloudsAnimationDesktop} 40s linear infinite;
    will-change: transform;
    backface-visibility: hidden;
    transform: translate3d(0, 0, 0);
    animation-play-state: ${({ $isPaused }) => ($isPaused ? 'paused' : 'running')};
  }
`;

const CarInterior = styled.div`
  position: absolute;
  bottom: -8px;
  left: 50%;
  width: 2560px;
  transform: translate3d(-50%, 0, 0);
  backface-visibility: hidden;
  contain: layout style;

  @media (min-width: 1921px) and (max-width: 2688px) {
    transform: translate3d(-50%, -110px, 0) scale(2);
  }

  @media (min-width: 2689px) {
    transform: translate3d(-50%, -400px, 0) scale(2);
  }
`;

const CarContainer = styled.div`
  width: 100%;
  will-change: transform;
  transition: transform 300ms ease-out;
  backface-visibility: hidden;
`;

const Car = styled(Image).attrs({
  priority: true,
})`
  width: 100%;
  transition: transform 300ms ease-out;
  will-change: transform;
  pointer-events: none;
`;

const ScreenForeground = styled(Box)<ScreenForegroundProps>`
  position: absolute;
  width: ${({ screenWrapperPosition }) => screenWrapperPosition.width};
  left: ${({ screenWrapperPosition }) => screenWrapperPosition.left};
  bottom: ${({ screenWrapperPosition }) => screenWrapperPosition.bottom};
  transition: transform 0.5s ease;
  will-change: transform;
  pointer-events: none;
  z-index: 4;
`;

const ScreenImg = styled(Image).attrs({
  priority: true,
})`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const Wheel = styled.div<WheelProps>`
  position: absolute;
  width: 1205px;
  left: ${({ wheelPosition }) => wheelPosition.left};
  bottom: ${({ wheelPosition }) => wheelPosition.bottom};
  transition: transform 300ms ease-out;
  pointer-events: none;
  will-change: transform;
  backface-visibility: hidden;
`;

const WheelImage = styled(Image).attrs({
  priority: true,
})`
  pointer-events: none;
`;

const buttonShadow = keyframes`
    0% {
        opacity: 0.5;
    }
    50% {
        opacity: 0;
    }
    100% {
        opacity: 0.3;
    }
`;

const GameButton = styled(InvisiButton)`
  width: 30px;
  height: 26px;
  top: 822px;
  left: 1344px;
  position: absolute;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg width='8' height='12' viewBox='0 0 8 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.10653 0.373469L7.05879 5.31909C7.22355 5.48432 7.31604 5.7081 7.31604 5.94144C7.31604 6.17479 7.22355 6.39864 7.05879 6.56388L2.11326 11.5094C1.99003 11.631 1.83369 11.7136 1.66381 11.747C1.49394 11.7803 1.31804 11.7628 1.15802 11.6968C0.997987 11.6308 0.860888 11.5191 0.763924 11.3757C0.66696 11.2323 0.614447 11.0635 0.612793 10.8904V0.992535C0.615698 0.820789 0.668698 0.653642 0.765321 0.511623C0.861943 0.369605 0.997936 0.25894 1.15662 0.193179C1.3153 0.127418 1.48972 0.109391 1.65847 0.141433C1.82723 0.173474 1.98299 0.254123 2.10653 0.373469Z' fill='%23FF0083'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
  ${({ theme }) => css`
    filter: drop-shadow(0px 1px 5px ${rgba(theme.colors.primary, 0.5)});
  `}

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-image: inherit;
    position: absolute;
    background-position: center;
    top: 0;
    left: 0;
    transform: scale(1.6) translate3d(0, 0, 0);
    filter: blur(3px);
    animation: ${buttonShadow} 2s infinite alternate;
    will-change: opacity;
    backface-visibility: hidden;
  }

  &.enter {
    opacity: 0;
  }
  &.enter-active {
    opacity: 1;
  }
  &.exit-active {
    opacity: 0;
  }
`;

interface TopFaderProps {
  hideUnderDesktop?: boolean;
}

const TopFader = styled(Box)`
  width: 100%;
  height: 50px;
  position: absolute;
  background: linear-gradient(180deg, white, rgba(255, 255, 255, 0));

  ${({ hideUnderDesktop = false }: TopFaderProps) =>
    hideUnderDesktop &&
    MEDIA_QUERIES.upToDesktop`
        display: none;
    `}
`;

const HonkButton = React.memo(styled(InvisiButton).attrs({
  'aria-hidden': true,
})`
  width: 74px;
  height: 74px;
  top: 918px;
  left: 782px;
  border-radius: 50%;
  background-color: transparent;
`);

const Driving: React.FC<DrivingProps> = ({
  title,
  mobileTitle,
  subtitle,
  belowCtaText,
  mode = 'default',
  showPlayButton = true,
  fadingMask = false,
  customUrl,
  screenWrapperPosition = DEFAULT_SCREEN_POSITION,
  wheelPosition = DEFAULT_WHEEL_POSITION,
  syncWheelYPosition = false,
  screenCanvasPosition,
  secondaryScreenView,
  checkPosition,
  overrideMobileAnimationStop = false,
  sceneTransform,
  randomSeed,
  roadGeneration,
  showMinimap,
  mobileTitlePaddingTop,
  excludedParams,
}) => {
  const { analytics } = useContext(CarPageContext);
  const isClient = useIsClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { isBelowDesktopViewport, isMobileViewport } = useViewportSize();
  const ref = useSectionEventOnViewportEntrance({
    sectionName: 'driving',
    page: analytics?.page,
    position: 1,
  });

  const drivingOptions = useMemo(
    () => ({
      mode,
      syncWheelYPosition,
      secondaryScreenView,
      overrideMobileAnimationStop,
      randomSeed,
      roadGeneration,
      showMinimap,
    }),
    [
      mode,
      syncWheelYPosition,
      secondaryScreenView,
      overrideMobileAnimationStop,
      randomSeed,
      roadGeneration,
      showMinimap,
    ],
  );

  const [
    elementRef,
    driving,
    {
      carRef,
      wheelRef,
      trackRef,
      sparksRef,
      isReady,
      isGameOn,
      isGameEnded,
      isScreenLow,
      isScreenCountingDown,
      isMuted,
      isMuteButtonVisible,
      isScrollPaused,
      currentTime,
      speed,
      points,
      highScore,
      gameTimer,
      gameTimerAddition,
      screenCountdown,
      screenBlinkType,
      activeCheerUps,
      play,
      replay,
      toggleMuted,
      honk,
    },
  ] = useDriving(canvasRef.current, drivingOptions);

  const screenProps = {
    gameTimerAddition,
    points,
    screenBlinkType,
    sparksRef,
    isScreenCountingDown,
    isGameOn,
    isMuted,
    isScreenLow,
    toggleMuted,
    isMuteButtonVisible,
    highScore,
    currentTime,
    gameTimer,
    screenCountdown,
  };

  return (
    <Section width={1} justifyContent="center" ref={ref}>
      <Container ref={elementRef} height={[725, 840, 880]} $isTeslaMode={mode === 'tesla'}>
        <Scene style={{ opacity: isReady ? 1 : 0 }} $sceneTransform={sceneTransform}>
          {mode !== 'tesla' && <Clouds $isPaused={isScrollPaused} />}
          <Canvas key="canvas" ref={canvasRef} mt={[-30, 30, 50]} />
          <CarInterior>
            <CarContainer ref={carRef}>
              {mode !== 'tesla' && <SpeedGauge speed={Math.ceil(speed)} />}
              {isClient &&
                (mode === 'tesla' ? (
                  <TeslaCarScreen
                    driving={driving}
                    fadingMask={fadingMask}
                    position={screenCanvasPosition}
                    trackPosition={screenWrapperPosition.track}
                    trackRef={trackRef}
                    checkPosition={checkPosition}
                    canvasScale={secondaryScreenView?.canvasScale}
                  />
                ) : (
                  <CarScreen {...screenProps} />
                ))}
              <Car
                key="car"
                alt="car Insurance by Lemonade"
                src={mode === 'tesla' ? carFsdUrl.src : carUrl.src}
                width={2560}
                height={1025}
              />
              <ScreenForeground
                screenWrapperPosition={screenWrapperPosition}
                style={{
                  transform: isScreenLow ? 'translateY(43px)' : 'none',
                }}
              >
                <ScreenImg
                  alt="Drive with telematics"
                  src={mode === 'tesla' ? screenFsdUrl.src : screenUrl.src}
                  width={354}
                  height={197}
                />
              </ScreenForeground>
              {isClient && showPlayButton && (
                <CSSTransition
                  in={!isBelowDesktopViewport && !isGameOn && !isScreenCountingDown}
                  unmountOnExit
                  mountOnEnter
                  timeout={500}
                >
                  <GameButton
                    onClick={play}
                    disabled={isGameOn || isScreenCountingDown}
                    role="button"
                    aria-hidden={true}
                  />
                </CSSTransition>
              )}
            </CarContainer>
            <Wheel
              key="wheel"
              ref={mode === 'tesla' ? undefined : (wheelRef as React.RefObject<HTMLDivElement>)}
              wheelPosition={wheelPosition}
            >
              {mode === 'tesla' && (
                <WheelImage
                  alt="Steering wheel"
                  ref={wheelRef as React.RefObject<HTMLImageElement>}
                  src={wheelFsdUrl.src}
                  width={363}
                  height={349}
                />
              )}
            </Wheel>
            <HonkButton onClick={honk} role="button" aria-label="Honk" />
          </CarInterior>
        </Scene>
        <Title
          isGameOn={isGameOn}
          isScreenCountingDown={isScreenCountingDown}
          title={isMobileViewport ? (mobileTitle ?? title) : title}
          subtitle={subtitle}
          belowCtaText={belowCtaText}
          customUrl={customUrl}
          excludedParams={excludedParams}
          mobilePaddingTop={mobileTitlePaddingTop}
        />
        <GameModeTitle
          isScreenCountdownZero={screenCountdown <= 0}
          isGameOn={isGameOn}
          isGameEnded={isGameEnded}
          isScreenCountingDown={isScreenCountingDown}
          isUpKeyDown={driving?.env.current.upKeyIsDown ?? false}
          isLeftKeyDown={driving?.env.current.leftKeyIsDown ?? false}
          isRightKeyDown={driving?.env.current.rightKeyIsDown ?? false}
          gameTimerState={gameTimer > 5 ? 'high' : gameTimer === 0 ? 'zero' : 'low'}
          onReplay={replay}
          activeCheerUps={activeCheerUps}
        />
        <TopFader hideUnderDesktop />
      </Container>
    </Section>
  );
};

export default React.memo(Driving);
